import type { ElementTemplate } from "@/types/game-data";

// --------------------------------------------------
// Militia Element Templates
// --------------------------------------------------

export const MILITIA_ELEMENTS: readonly ElementTemplate[] = [
	{
		id: "militia-command",
		faction: "militia",
		type: "command",
		name: "Command Element",
		description:
			"1 Commander + optional 1-5 Soldiers. May be equipped with Command and Specialist Equipment.",
		minModels: 1,
		maxModels: 6,
		hasCommander: true,
		allowedTiers: ["militia"],
		commanderCosts: { militia: 40 },
		soldierCosts: { militia: 20 },
		sergeantUpgradeCost: 0,
		allowSergeant: false,
		specialistWeaponCategories: ["machinegun", "marksman", "launcher", "shotgun", "specialist"],
		specialistWeaponRatio: 2, // 1 in 2
		equipmentAccess: ["standard", "command", "specialist"],
	},
	{
		id: "militia-fighter",
		faction: "militia",
		type: "fighter",
		name: "Fighter Element",
		description:
			"1-19 Fighters. May upgrade one model to Sergeant for 10r. Standard equipment only.",
		minModels: 1,
		maxModels: 20,
		hasCommander: false,
		allowedTiers: ["militia"],
		commanderCosts: {},
		soldierCosts: { militia: 20 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: ["machinegun", "launcher", "shotgun", "marksman"],
		specialistWeaponRatio: 3, // 1 in 3
		equipmentAccess: ["standard"],
	},
	{
		id: "militia-weapons",
		faction: "militia",
		type: "weapons",
		name: "Weapons Element",
		description:
			"1-2 Fighters with specialist weapons. If 2 fighters, may choose an Emplaced Weapon.",
		minModels: 1,
		maxModels: 2,
		hasCommander: false,
		allowedTiers: ["militia"],
		commanderCosts: {},
		soldierCosts: { militia: 20 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: [
			"machinegun",
			"marksman",
			"launcher",
			"shotgun",
			"specialist",
			"emplaced",
		],
		specialistWeaponRatio: 1, // all may take specialist
		equipmentAccess: ["standard", "specialist"],
	},
	{
		id: "militia-mercenary",
		faction: "militia",
		type: "mercenary",
		name: "Mercenary Element",
		description:
			"1-12 Fighters with access to specialist equipment. May upgrade one to Sergeant for 10r.",
		minModels: 1,
		maxModels: 12,
		hasCommander: false,
		allowedTiers: ["militia"],
		commanderCosts: {},
		soldierCosts: { militia: 20 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: ["machinegun", "marksman", "launcher", "shotgun", "specialist"],
		specialistWeaponRatio: 2, // 1 in 2
		equipmentAccess: ["standard", "command", "specialist"],
	},
] as const;

// --------------------------------------------------
// Task Force Element Templates
// --------------------------------------------------

export const TASKFORCE_ELEMENTS: readonly ElementTemplate[] = [
	{
		id: "tf-command",
		faction: "taskforce",
		type: "command",
		name: "Command Element",
		description:
			"1 Commander + optional 1-5 Soldiers. Full equipment and specialist access. T1/T2 get additional equipment.",
		minModels: 1,
		maxModels: 6,
		hasCommander: true,
		allowedTiers: ["tier1", "tier2", "veteran", "enlisted"],
		commanderCosts: { tier1: 80, tier2: 60, veteran: 50, enlisted: 40 },
		soldierCosts: { tier1: 60, tier2: 50, veteran: 40, enlisted: 30 },
		sergeantUpgradeCost: 0,
		allowSergeant: false,
		specialistWeaponCategories: ["machinegun", "marksman", "launcher", "shotgun", "specialist"],
		specialistWeaponRatio: 2,
		equipmentAccess: ["standard", "command", "specialist", "tier1and2"],
	},
	{
		id: "tf-infantry",
		faction: "taskforce",
		type: "infantry",
		name: "Infantry Element",
		description:
			"1-7 Soldiers. Main combat element. May upgrade one to Sergeant for 10r. T1/T2 get additional equipment.",
		minModels: 1,
		maxModels: 8,
		hasCommander: false,
		allowedTiers: ["tier1", "tier2", "veteran", "enlisted"],
		commanderCosts: {},
		soldierCosts: { tier1: 60, tier2: 50, veteran: 40, enlisted: 30 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: ["machinegun", "marksman", "launcher", "shotgun", "specialist"],
		specialistWeaponRatio: 2,
		equipmentAccess: ["standard", "tier1and2"],
	},
	{
		id: "tf-support",
		faction: "taskforce",
		type: "support",
		name: "Support Element (Fire Team)",
		description: "1-2 Soldiers with heavy/specialist weapons. May upgrade one to Sergeant for 10r.",
		minModels: 1,
		maxModels: 2,
		hasCommander: false,
		allowedTiers: ["tier1", "tier2", "veteran", "enlisted"],
		commanderCosts: {},
		soldierCosts: { tier1: 80, tier2: 60, veteran: 50, enlisted: 40 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: ["machinegun", "launcher", "specialist"],
		specialistWeaponRatio: 1, // all may take specialist
		equipmentAccess: ["standard", "tier1and2"],
	},
	{
		id: "tf-specialist",
		faction: "taskforce",
		type: "specialist",
		name: "Specialist Element (Marksman/Recon)",
		description: "1-4 Soldiers. Marksman/Recon role with Command and Specialist equipment access.",
		minModels: 1,
		maxModels: 4,
		hasCommander: false,
		allowedTiers: ["tier1", "tier2", "veteran", "enlisted"],
		commanderCosts: {},
		soldierCosts: { tier1: 60, tier2: 50, veteran: 40, enlisted: 30 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: ["machinegun", "marksman", "specialist"],
		specialistWeaponRatio: 1, // all may take specialist
		equipmentAccess: ["standard", "command", "specialist", "tier1and2"],
	},
] as const;

// --------------------------------------------------
// Russian Forces Element Templates
// --------------------------------------------------

export const RUSSIAN_ELEMENTS: readonly ElementTemplate[] = [
	{
		id: "ru-conscript",
		faction: "russian",
		type: "conscript-infantry",
		name: "Conscript Element",
		description:
			"110r for 6 Conscript fighters and 1 Conscript Sergeant, all equipped with Medium Rifles. Represents fighters with very little or no training. 1 fighter may take an Assault LMG (28r) or RPG HEAT (30r). The entire element may take Body Armour (10r per model).",
		minModels: 7,
		maxModels: 7,
		hasCommander: false,
		allowedTiers: ["conscript"],
		commanderCosts: {},
		soldierCosts: { conscript: 0 },
		sergeantUpgradeCost: 0,
		allowSergeant: true,
		specialistWeaponCategories: ["machinegun", "launcher"],
		specialistWeaponRatio: 7, // 1 in 7 can take specialist
		equipmentAccess: ["standard"],
		baseCost: 110,
		additionalModelCost: 0,
		includedGear: ["medium-rifle"],
	},
	{
		id: "ru-infantry-squad",
		faction: "russian",
		type: "infantry",
		name: "Russian Infantry Squad",
		description:
			"150r for 6 Enlisted fighters and 1 Enlisted Sergeant, all equipped with Medium Rifles and Body Armour. Represents the bulk of Russian Army and Naval Infantry. Up to 2 may take Grenade Launchers. 1 may take an Assault LMG (28r) or MMG. 1 may take a Launcher from the Russian launcher list.",
		minModels: 7,
		maxModels: 7,
		hasCommander: false,
		allowedTiers: ["enlisted"],
		commanderCosts: {},
		soldierCosts: { enlisted: 0 },
		sergeantUpgradeCost: 0,
		allowSergeant: true,
		specialistWeaponCategories: ["machinegun", "launcher"],
		specialistWeaponRatio: 4, // roughly 2 of 7 can take specialist
		equipmentAccess: ["standard"],
		baseCost: 150,
		additionalModelCost: 0,
		includedGear: ["medium-rifle", "body-armour"],
	},
	{
		id: "ru-sso",
		faction: "russian",
		type: "special-ops",
		name: "Russian SSO Element",
		description:
			"150r for 2 Tier 2 soldiers. Up to 4 additional soldiers for 50r each. Each equipped with Medium Rifle and Frag Grenades. Each may be upgraded to Tier 1 for 20r. 1 in 2 may be Specialist with Machine Gun, Marksman, Launcher, Shotgun, or Unique weapons.",
		minModels: 2,
		maxModels: 6,
		hasCommander: false,
		allowedTiers: ["tier2", "tier1"],
		commanderCosts: {},
		soldierCosts: { tier2: 0, tier1: 20 },
		sergeantUpgradeCost: 0,
		allowSergeant: false,
		specialistWeaponCategories: [
			"rifle",
			"smg",
			"shotgun",
			"sidearm",
			"marksman",
			"machinegun",
			"launcher",
			"closecombat",
			"thrown",
			"specialist",
		],
		specialistWeaponRatio: 2,
		equipmentAccess: ["standard", "russian-sso", "specialist"],
		baseCost: 150,
		additionalModelCost: 50,
		includedGear: ["medium-rifle", "frag-grenade"],
	},
	{
		id: "ru-heavy-breacher",
		faction: "russian",
		type: "breacher",
		name: "Heavy Breacher Element",
		description:
			"150r for 1 Sergeant and 3 Breachers, all equipped with Pistol and Frag Suits. Represents GRU or FSB Breaching teams. Up to 4 additional Breachers for 38r each. Any model may take weapons from SMG, Sidearms, Shotgun, Rifle, Launcher, Thrown, and Close Combat armouries. May upgrade to Tier 2 for 10r each.",
		minModels: 4,
		maxModels: 8,
		hasCommander: false,
		allowedTiers: ["veteran", "tier2"],
		commanderCosts: {},
		soldierCosts: { veteran: 0, tier2: 10 },
		sergeantUpgradeCost: 0,
		allowSergeant: true,
		specialistWeaponCategories: [
			"rifle",
			"smg",
			"shotgun",
			"sidearm",
			"machinegun",
			"launcher",
			"closecombat",
			"thrown",
		],
		specialistWeaponRatio: 1,
		equipmentAccess: ["standard", "russian-breacher", "specialist"],
		baseCost: 150,
		additionalModelCost: 38,
		includedGear: ["pistol", "frag-suit"],
	},
] as const;

// --------------------------------------------------
// Law Enforcement Element Templates
// --------------------------------------------------

export const LAW_ENFORCEMENT_ELEMENTS: readonly ElementTemplate[] = [
	{
		id: "le-swat-team",
		faction: "law-enforcement",
		type: "tactical",
		name: "SWAT Team Element",
		description:
			"140r for 1 Sergeant and 4 Officers, each equipped with Medium Rifle, Pistol, and Taser. Up to 4 additional Officers for 35r each. May replace Medium Rifle with Rifle, SMG, or Sidearm. 2 in 4 may be Specialist with Shotgun, Marksman, or Unique weapons. May upgrade to Veterans for 10r each.",
		minModels: 5,
		maxModels: 9,
		hasCommander: false,
		allowedTiers: ["enlisted", "veteran"],
		commanderCosts: {},
		soldierCosts: { enlisted: 0, veteran: 10 },
		sergeantUpgradeCost: 0,
		allowSergeant: true,
		specialistWeaponCategories: [
			"rifle",
			"smg",
			"sidearm",
			"shotgun",
			"marksman",
			"thrown",
			"specialist",
			"launcher",
		],
		specialistWeaponRatio: 2,
		equipmentAccess: ["law-enforcement", "law-enforcement-specialist"],
		baseCost: 140,
		additionalModelCost: 35,
		includedGear: ["medium-rifle", "pistol", "taser"],
	},
	{
		id: "le-patrol-cop",
		faction: "law-enforcement",
		type: "patrol",
		name: "Patrol Cop Element",
		description:
			"40r for 2 Police Officers equipped with Pistols and Tasers. One may also take a Shotgun (12r) or Medium Rifle (10r). Each may take Body Armour (10r).",
		minModels: 2,
		maxModels: 2,
		hasCommander: false,
		allowedTiers: ["enlisted"],
		commanderCosts: {},
		soldierCosts: { enlisted: 0 },
		sergeantUpgradeCost: 0,
		allowSergeant: false,
		specialistWeaponCategories: ["shotgun", "rifle"],
		specialistWeaponRatio: 2, // 1 of 2 may take extra weapon
		equipmentAccess: ["standard"],
		baseCost: 40,
		additionalModelCost: 0,
		includedGear: ["pistol", "taser"],
	},
] as const;

// --------------------------------------------------
// Criminal Element Templates
// --------------------------------------------------

export const CRIMINAL_ELEMENTS: readonly ElementTemplate[] = [
	{
		id: "ce-crime-boss",
		faction: "criminal-element",
		type: "boss",
		name: "Crime Boss Element",
		description:
			"30r for a single Commander. Up to 6 bodyguards for 20r each. Commander and bodyguards equipped with Personal Defence Weapon. Any model may choose additional weapons from the armoury and items from the Equipment list.",
		minModels: 1,
		maxModels: 7,
		hasCommander: true,
		allowedTiers: ["criminal"],
		commanderCosts: { criminal: 0 },
		soldierCosts: { criminal: 0 },
		sergeantUpgradeCost: 0,
		allowSergeant: false,
		specialistWeaponCategories: [
			"rifle",
			"smg",
			"shotgun",
			"sidearm",
			"closecombat",
			"thrown",
		],
		specialistWeaponRatio: 1,
		equipmentAccess: ["criminal-basic"],
		baseCost: 30,
		additionalModelCost: 20,
		includedGear: ["personal-defence-weapon"],
	},
	{
		id: "ce-criminal-gang",
		faction: "criminal-element",
		type: "gang",
		name: "Criminal Gang Element",
		description:
			"40r for 2 Gangsters. Up to 18 additional fighters for 20r each. One may be upgraded to Sergeant for 10r. Each equipped with Personal Defence Weapon. May choose additional weapons from the armoury.",
		minModels: 2,
		maxModels: 20,
		hasCommander: false,
		allowedTiers: ["criminal"],
		commanderCosts: {},
		soldierCosts: { criminal: 0 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: [
			"rifle",
			"smg",
			"shotgun",
			"marksman",
			"launcher",
			"thrown",
		],
		specialistWeaponRatio: 1,
		equipmentAccess: ["criminal-basic"],
		baseCost: 40,
		additionalModelCost: 20,
		includedGear: ["personal-defence-weapon"],
	},
	{
		id: "ce-organised-criminal",
		faction: "criminal-element",
		type: "gang",
		name: "Organised Criminal Element",
		description:
			"80r for 2 Veteran Gangsters. Up to 4 additional for 40r each. One may be upgraded to Sergeant for 10r. Each equipped with Personal Defence Weapon. Full access to Rifles, Launchers, Machine Guns, and extensive equipment.",
		minModels: 2,
		maxModels: 6,
		hasCommander: false,
		allowedTiers: ["veteran"],
		commanderCosts: {},
		soldierCosts: { veteran: 0 },
		sergeantUpgradeCost: 10,
		allowSergeant: true,
		specialistWeaponCategories: [
			"rifle",
			"smg",
			"shotgun",
			"sidearm",
			"marksman",
			"machinegun",
			"launcher",
			"closecombat",
			"thrown",
		],
		specialistWeaponRatio: 1,
		equipmentAccess: ["criminal-organised"],
		baseCost: 80,
		additionalModelCost: 40,
		includedGear: ["personal-defence-weapon"],
	},
] as const;

// --------------------------------------------------
// ALL ELEMENTS combined
// --------------------------------------------------

export const ALL_ELEMENTS: readonly ElementTemplate[] = [
	...MILITIA_ELEMENTS,
	...TASKFORCE_ELEMENTS,
	...RUSSIAN_ELEMENTS,
	...LAW_ENFORCEMENT_ELEMENTS,
	...CRIMINAL_ELEMENTS,
];
